import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { type VariantProps } from 'class-variance-authority';
import { Check } from 'lucide-react';
import * as React from 'react';
import { checkboxVariants } from '@/shared/constants/ui-variants';
import { cn } from '@/shared/lib/utils';
import { CheckboxSize } from '@/shared/types/checkbox.types';

interface CheckboxProps
  extends
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

/**
 * Tick icon size kept slightly smaller than the box so it stays optically
 * centered with a small inner gutter at every checkbox size.
 */
const ICON_SIZE_CLASSES: Readonly<Record<CheckboxSize, string>> = {
  [CheckboxSize.Small]: 'size-3',
  [CheckboxSize.Default]: 'size-3.5',
  [CheckboxSize.Large]: 'size-4',
};

const Checkbox = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, size, variant, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ size, variant }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn('flex items-center justify-center text-current')}
    >
      <Check
        className={ICON_SIZE_CLASSES[size ?? CheckboxSize.Default]}
        strokeWidth={3}
      />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
export type { CheckboxProps };
export { CheckboxSize, CheckboxVariant } from '@/shared/types/checkbox.types';
